
import { useState } from 'react'
import { CATEGORY_TYPICAL_W, formatW, wattsToVa } from '@/lib/power'
import { CATEGORY_META, type DeviceCategory } from '@/lib/categories'
import type { BuiltPowerPlan, PowerPlanSnapshot } from '@/lib/power-plan'
import { btn, card, cx, notice, tag, tagTone } from '@/lib/ui'
import { PowerFigureForm } from './PowerFigureForm'
import type { PowerDevicePick } from './PowerPlanClient'

/**
 * What is missing, named, with a magnitude.
 *
 * A count of unknowns can be dismissed; a named box with a plausible size
 * cannot. The estimate here is never added to anything — it exists only to say
 * how wrong the total could be, so the gap gets closed before the order goes in.
 */
export function PowerGapsPanel({
  built,
  deviceDetail,
  snapshot,
  apiBase,
  onFigureSaved,
}: {
  built: BuiltPowerPlan
  deviceDetail: Map<string, PowerDevicePick>
  snapshot: PowerPlanSnapshot
  apiBase: string
  onFigureSaved: () => void
}) {
  // Which row is being filled in. One at a time: this sits inside a panel of
  // eleven-pixel type, and two open forms would bury the list they came from.
  const [filling, setFilling] = useState<string | null>(null)
  const unknown = built.rows.filter((row) => row.zeroReason === 'UNKNOWN_DRAW')
  if (unknown.length === 0 && built.warnings.length === 0) return null

  const itemById = new Map(snapshot.items.map((item) => [item.id, item]))
  const sized = unknown.map((row) => {
    const item = itemById.get(row.id)
    const device = item?.deviceId ? deviceDetail.get(item.deviceId) : undefined
    const category = device?.category ?? 'OTHER'
    const each = CATEGORY_TYPICAL_W[category] ?? CATEGORY_TYPICAL_W.OTHER
    // Only a catalogued device can take a figure; a free-text row is a line on
    // one plan and has nothing to attach it to.
    return { row, category, watts: each * row.qty, deviceId: item?.deviceId ?? null, device }
  })

  const worstMissingW = sized.reduce((sum, entry) => sum + entry.watts, 0)
  const worstMissingVa = wattsToVa(worstMissingW, 0.9)
  const shareOfTotal =
    built.totals.vaConnected > 0 ? (worstMissingVa / built.totals.vaConnected) * 100 : 0
  const biggest = sized.reduce<(typeof sized)[number] | null>(
    (worst, entry) => (worst === null || entry.watts > worst.watts ? entry : worst),
    null,
  )

  return (
    <section className={cx(card, 'px-5 py-4')} aria-label="Gaps">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-medium">Gaps</h2>
        {unknown.length > 0 && (
          <span className={cx(tag, tagTone.warning)}>{unknown.length} without a figure</span>
        )}
        <span className="text-[11px] text-muted">
          {built.coverage.knownWattsPct.toFixed(0)}% of the modelled watts are published figures
        </span>
      </div>

      {/* Fires only when the missing rows could plausibly move the answer by
          more than a tenth. Below that it is noise, and a warning that cries
          wolf on a 20 W converter gets ignored on a 900 W edit machine. */}
      {shareOfTotal >= 10 && biggest && (
        <p className={cx(notice.warning, 'mt-3')}>
          {unknown.length} {unknown.length === 1 ? 'row has' : 'rows have'} no figure — one is a{' '}
          <strong className="font-medium">{categorySingular(biggest.category)}</strong>, typically
          around {formatW(CATEGORY_TYPICAL_W[biggest.category] ?? 100)} on its own.{' '}
          <strong className="font-medium">
            This total could be low by {shareOfTotal < 100 ? `${Math.round(shareOfTotal)}%` : 'more than double'}.
          </strong>{' '}
          None of that is in the numbers above.
        </p>
      )}

      {unknown.length > 0 && (
        <ul className="mt-3 space-y-1">
          {sized.map(({ row, category, watts, deviceId, device }) => (
            <li key={row.id} className="text-[11px]">
              <div className="flex items-baseline justify-between gap-3">
                <span className="truncate">
                  {row.qty > 1 && <span className="text-muted">{row.qty} × </span>}
                  {row.label}
                  <span className="ml-1.5 text-muted">{categoryLabel(category)}</span>
                </span>
                <span className="flex shrink-0 items-baseline gap-2">
                  <span className="tabular-nums text-muted">could be ~{formatW(watts)} — not counted</span>
                  {deviceId && filling !== row.id && (
                    <button
                      type="button"
                      className={cx(btn.sm, btn.ghost, 'px-2 py-0.5 text-[11px]')}
                      onClick={() => setFilling(row.id)}
                    >
                      I know this one
                    </button>
                  )}
                </span>
              </div>
              {deviceId && filling === row.id && (
                <PowerFigureForm
                  deviceId={deviceId}
                  deviceLabel={device ? `${device.manufacturer} ${device.model}` : row.label}
                  apiBase={apiBase}
                  onSaved={() => {
                    setFilling(null)
                    onFigureSaved()
                  }}
                  onCancel={() => setFilling(null)}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {built.warnings.length > 0 && (
        <ul className="mt-3 space-y-1.5 border-t border-[var(--border)] pt-3">
          {built.warnings.map((warning) => (
            <li key={warning} className="text-[11px] text-warning">
              {warning}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/** A catalogue category, or the plain word when a row is free text. */
function categoryLabel(category: string): string {
  return CATEGORY_META[category as DeviceCategory]?.label ?? 'Unknown type'
}

/** "a Computer", not "a Computers" — this sits mid-sentence. */
function categorySingular(category: string): string {
  return CATEGORY_META[category as DeviceCategory]?.singular ?? 'device'
}
